import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, User, MapPin } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../../components/ui/Card';
import { SeverityIndicator } from '../../components/ui/SeverityIndicator';
// Mock data for active report
const mockActiveReport = {
  id: 'r1',
  status: 'inProgress' as const,
  problemType: 'Oggetto Rotto',
  problemEmoji: '🔧',
  description: 'Il rubinetto del bagno principale perde acqua costantemente. La perdita è abbastanza consistente e bagnando il mobile sottostante.',
  severity: 4,
  room: 'Bagno',
  reportedBy: 'Mario R. - Fornitore Pulizie',
  reportDate: '28 Gen 2025, 10:32',
  assignedTo: 'IdraulicoPro Srl',
  estimatedTime: '~2 ore',
  photos: ['https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/8da17c5b-c2d1-4b3e-aa33-a0df921816fa.jpeg?aki_policy=xx_large', 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/22e98b9a-4848-48d2-8bc9-f75adab5a84d.jpeg?im_w=1200'],
  timeline: [{
    status: 'Inviata',
    timestamp: '28 Gen, 10:32',
    completed: true
  }, {
    status: 'In Revisione',
    timestamp: '28 Gen, 10:45',
    completed: true
  }, {
    status: 'Presa in carico',
    timestamp: '28 Gen, 11:00',
    completed: true
  }, {
    status: 'In lavorazione',
    timestamp: '28 Gen, 11:30',
    completed: true
  }, {
    status: 'Risolta',
    timestamp: '',
    completed: false
  }]
};
const statusConfig = {
  submitted: {
    label: 'Inviata',
    color: 'bg-gray-100 text-gray-700'
  },
  review: {
    label: 'In Revisione',
    color: 'bg-blue-100 text-blue-700'
  },
  assigned: {
    label: 'Presa in carico',
    color: 'bg-amber-100 text-amber-700'
  },
  inProgress: {
    label: 'In lavorazione',
    color: 'bg-red-100 text-red-700'
  },
  resolved: {
    label: 'Risolta',
    color: 'bg-green-100 text-green-700'
  }
};
export function ActiveReportDetailPage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-947">
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 pb-12" data-id="element-948">
        {/* Header */}
        <motion.div className="flex items-center gap-4 mb-8" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-949">
          <button onClick={() => navigate('/host')} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-950">
            <ArrowLeft className="w-6 h-6 text-gray-600" data-id="element-951" />
          </button>
          <div className="flex-1" data-id="element-952">
            <h1 className="text-2xl font-bold text-navy" data-id="element-953">
              Segnalazione Attiva
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-954">
              ID: {mockActiveReport.id}
            </p>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${statusConfig[mockActiveReport.status].color}`} data-id="element-955">
            {statusConfig[mockActiveReport.status].label}
          </span>
        </motion.div>

        {/* Status Timeline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="mb-6" data-id="element-956">
          <Card className="p-6" data-id="element-957">
            <h3 className="text-lg font-bold text-navy mb-4" data-id="element-958">
              Stato Avanzamento
            </h3>
            <div className="space-y-3" data-id="element-959">
              {mockActiveReport.timeline.map((item, index) => <div key={index} className="flex items-center gap-4" data-id="element-960">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${item.completed ? 'bg-teal-deep' : 'bg-gray-300'}`} data-id="element-961" />
                  <div className="flex-1" data-id="element-962">
                    <p className={`text-sm font-medium ${item.completed ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-963">
                      {item.status}
                    </p>
                    {item.timestamp && <p className="text-xs text-gray-500" data-id="element-964">{item.timestamp}</p>}
                  </div>
                </div>)}
            </div>

            {mockActiveReport.estimatedTime && <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-965">
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-966">
                  <Clock className="w-4 h-4" data-id="element-967" />
                  <span data-id="element-968">
                    Tempo stimato per risoluzione:{' '}
                    <strong data-id="element-969">{mockActiveReport.estimatedTime}</strong>
                  </span>
                </div>
              </div>}
          </Card>
        </motion.div>

        {/* Problem Details */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="mb-6" data-id="element-970">
          <Card className="p-6" data-id="element-971">
            <h3 className="text-lg font-bold text-navy mb-4" data-id="element-972">
              Dettagli Problema
            </h3>

            <div className="space-y-4" data-id="element-973">
              <div className="flex items-center gap-3" data-id="element-974">
                <span className="text-3xl" data-id="element-975">
                  {mockActiveReport.problemEmoji}
                </span>
                <div data-id="element-976">
                  <p className="font-semibold text-gray-900" data-id="element-977">
                    {mockActiveReport.problemType}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-1" data-id="element-978">
                    <MapPin className="w-4 h-4" data-id="element-979" />
                    <span data-id="element-980">{mockActiveReport.room}</span>
                  </div>
                </div>
              </div>

              <div data-id="element-981">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-982">
                  Descrizione
                </p>
                <p className="text-gray-700" data-id="element-983">{mockActiveReport.description}</p>
              </div>

              <div data-id="element-984">
                <SeverityIndicator label="Gravità segnalata" value={mockActiveReport.severity} interactive={false} showLabel={true} size="sm" variant="severity" data-id="element-985" />
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Photos */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mb-6" data-id="element-986">
          <Card className="p-6" data-id="element-987">
            <h3 className="text-lg font-bold text-navy mb-4" data-id="element-988">Foto Problema</h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-989">
              {mockActiveReport.photos.map((photo, index) => <img key={index} src={photo} alt={`Foto ${index + 1}`} className="w-full aspect-[4/3] object-cover rounded-lg" data-id="element-990" />)}
            </div>
          </Card>
        </motion.div>

        {/* Report Info */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} data-id="element-991">
          <Card className="p-6" data-id="element-992">
            <h3 className="text-lg font-bold text-navy mb-4" data-id="element-993">
              Informazioni Segnalazione
            </h3>

            <div className="space-y-3 text-sm" data-id="element-994">
              <div className="flex items-start gap-3" data-id="element-995">
                <User className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-996" />
                <div data-id="element-997">
                  <p className="text-gray-500" data-id="element-998">Segnalato da</p>
                  <p className="font-medium text-gray-900" data-id="element-999">
                    {mockActiveReport.reportedBy}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-1000">
                <Clock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-1001" />
                <div data-id="element-1002">
                  <p className="text-gray-500" data-id="element-1003">Data segnalazione</p>
                  <p className="font-medium text-gray-900" data-id="element-1004">
                    {mockActiveReport.reportDate}
                  </p>
                </div>
              </div>

              {mockActiveReport.assignedTo && <div className="flex items-start gap-3" data-id="element-1005">
                  <User className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-1006" />
                  <div data-id="element-1007">
                    <p className="text-gray-500" data-id="element-1008">Assegnato a</p>
                    <p className="font-medium text-gray-900" data-id="element-1009">
                      {mockActiveReport.assignedTo}
                    </p>
                  </div>
                </div>}
            </div>
          </Card>
        </motion.div>
      </main>
    </div>;
}